import { useMemo, useState } from "react";
import { X, Loader2, ChevronLeft, ChevronRight, CheckCircle2, Flag, AlertTriangle, Plus, ClipboardCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

const Mono = ({ children, className = "" }) => (
  <span className={`font-mono uppercase tracking-[0.2em] ${className}`}>{children}</span>
);
const inp = "w-full bg-navy-950 border border-gold-500/20 px-3 py-2.5 text-white text-sm focus:outline-none focus:border-gold-500";
const lbl = "font-mono uppercase tracking-[0.2em] text-[0.55rem] text-white/45 block mb-1.5";

const EMPTY_LINE = {
  name: "", site_number: "", asset_type: "Road - sealed", funding_measure: "EPAR",
  emergency_works: false, restoration_works: false,
  road_name: "", road_id: "", carriageway: "", chainage_start: "", chainage_end: "", where_on_asset: "",
  damage_type: "", treatment_type: "", unit_of_measure: "", quantity: "",
  cet_cost: "", council_cost: "", cost_justification: "", notes: "",
};

const STEPS = ["Identity", "Location", "Damage & treatment", "Costs", "Review line"];

export default function LineBuilder({ claimId, meta, region, onClose, onSaved }) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY_LINE);
  const [decision, setDecision] = useState("approved");
  const [reviewNote, setReviewNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [savedCount, setSavedCount] = useState(0);
  const [justSaved, setJustSaved] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onTreatment = (name) => {
    const t = meta.treatments.find((x) => x.name === name);
    if (!t) { set("treatment_type", ""); return; }
    setForm((f) => {
      const qty = Number(f.quantity) || 0;
      const r = t.rates?.[region];
      return { ...f, treatment_type: t.name, unit_of_measure: t.unit, cet_cost: r != null && qty ? Math.round(r * qty * 100) / 100 : f.cet_cost };
    });
  };
  const onQty = (v) => {
    setForm((f) => {
      const qty = Number(v) || 0;
      const t = meta.treatments.find((x) => x.name === f.treatment_type);
      const r = t?.rates?.[region];
      return { ...f, quantity: v, cet_cost: r != null && qty ? Math.round(r * qty * 100) / 100 : f.cet_cost };
    });
  };

  const selectedTreatment = meta.treatments.find((t) => t.name === form.treatment_type);
  const rate = selectedTreatment?.rates?.[region];
  const cet = Number(form.cet_cost) || 0, council = Number(form.council_cost) || 0;
  const variance = cet && form.council_cost !== "" ? ((council - cet) / cet) * 100 : null;
  const needJust = variance != null && Math.abs(variance) >= 5 && !form.cost_justification;

  const canNext = useMemo(() => (step === 0 ? form.name.trim().length > 0 : true), [step, form.name]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const numeric = new Set(["quantity", "cet_cost", "council_cost"]);
      const payload = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, numeric.has(k) ? (v === "" ? null : Number(v)) : v]));
      const { data } = await api.post(`/fieldclaim/claims/${claimId}/assets`, payload);
      if (decision !== "pending" || reviewNote) {
        await api.post(`/fieldclaim/assets/${data.id}/review`, { status: decision, note: reviewNote || null });
      }
      setSavedCount((c) => c + 1);
      setJustSaved(data);
      onSaved();
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || "Could not save this line.");
    } finally { setSaving(false); }
  };

  const nextLine = () => { setForm(EMPTY_LINE); setDecision("approved"); setReviewNote(""); setStep(0); setJustSaved(null); setErr(""); };

  const summary = [
    ["Asset", form.name], ["Type", form.asset_type], ["Measure", form.funding_measure],
    ["Road", [form.road_name, form.road_id].filter(Boolean).join(" · ") || "—"],
    ["Chainage", form.chainage_start ? `${form.chainage_start} → ${form.chainage_end || "—"}` : "—"],
    ["Damage", form.damage_type || "—"], ["Treatment", form.treatment_type || "—"],
    ["Quantity", form.quantity ? `${form.quantity} ${form.unit_of_measure}` : "—"],
    ["Regional benchmark", form.cet_cost !== "" ? `$${Number(form.cet_cost).toLocaleString()}` : "—"],
    ["Council cost", form.council_cost !== "" ? `$${Number(form.council_cost).toLocaleString()}` : "—"],
    ["Variance", variance == null ? "—" : `${variance >= 0 ? "+" : ""}${variance.toFixed(0)}%`],
  ];

  return (
    <div className="fixed inset-0 bg-navy-950/85 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="line-builder-modal"
        className="bg-navy-900 border border-gold-500/30 w-full max-w-2xl max-h-[92vh] overflow-y-auto text-white">
        {/* header */}
        <div className="px-6 pt-5 pb-4 border-b border-gold-500/20 sticky top-0 bg-navy-900 z-10">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-xl flex items-center gap-2"><ClipboardCheck className="h-5 w-5 text-gold-400" /> Claim line builder</h2>
            <div className="flex items-center gap-3">
              {savedCount > 0 && <Mono className="text-[0.5rem] text-emerald-300" data-testid="lb-saved-count">{savedCount} line{savedCount > 1 ? "s" : ""} added</Mono>}
              <button onClick={onClose} data-testid="lb-close"><X className="h-5 w-5 text-white/60" /></button>
            </div>
          </div>
          {!justSaved && (
            <div className="flex items-center gap-1 mt-4">
              {STEPS.map((s, i) => (
                <button key={s} onClick={() => i < step && setStep(i)} data-testid={`lb-step-ind-${i}`}
                  className={`flex-1 group ${i > step ? "cursor-default" : ""}`}>
                  <div className={`h-1 rounded-full transition-colors ${i < step ? "bg-emerald-400" : i === step ? "bg-gold-500" : "bg-navy-800"}`} />
                  <Mono className={`text-[0.42rem] mt-1.5 block text-center ${i === step ? "text-gold-300" : "text-white/30"}`}>{i + 1}. {s}</Mono>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 space-y-5">
          {justSaved ? (
            <div className="text-center py-8 space-y-5" data-testid="lb-saved-screen">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto" />
              <div>
                <div className="font-heading text-2xl">Line saved — {justSaved.name}</div>
                <p className="text-white/50 text-sm font-light mt-1">
                  Review: <span className={decision === "approved" ? "text-emerald-300" : decision === "flagged" ? "text-red-300" : "text-white/60"}>{decision}</span>.
                  Capture photo evidence from the asset panel or the field app.
                </p>
              </div>
              <div className="flex items-center justify-center gap-3">
                <button onClick={nextLine} data-testid="lb-add-next" className="bg-gold-500 text-navy-950 font-semibold px-6 py-3 rounded-sm hover:bg-gold-400 transition-colors flex items-center gap-2">
                  <Plus className="h-4 w-4" /> Add next line
                </button>
                <button onClick={onClose} data-testid="lb-done" className="border border-gold-500/40 text-gold-300 px-6 py-3 hover:bg-gold-500/10 transition-colors">Done</button>
              </div>
            </div>
          ) : (
            <>
              {step === 0 && (
                <div className="space-y-4" data-testid="lb-step-0">
                  <div>
                    <label className={lbl}>Asset / line name *</label>
                    <input autoFocus value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Wilsons Creek Rd — causeway 2" className={inp} data-testid="lb-name" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Asset type</label>
                      <select value={form.asset_type} onChange={(e) => set("asset_type", e.target.value)} className={inp} data-testid="lb-asset-type">
                        {meta.asset_types.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select></div>
                    <div><label className={lbl}>Funding measure</label>
                      <select value={form.funding_measure} onChange={(e) => set("funding_measure", e.target.value)} className={inp} data-testid="lb-measure">
                        {meta.funding_measures.map((m) => <option key={m.code} value={m.code}>{m.code} — {m.name}</option>)}
                      </select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Site number</label><input value={form.site_number} onChange={(e) => set("site_number", e.target.value)} className={inp} data-testid="lb-siteno" /></div>
                    <div className="flex items-end gap-4 pb-1">
                      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.emergency_works} onChange={(e) => set("emergency_works", e.target.checked)} className="accent-gold-500" data-testid="lb-ew" /> Emergency works</label>
                      <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer"><input type="checkbox" checked={form.restoration_works} onChange={(e) => set("restoration_works", e.target.checked)} className="accent-gold-500" data-testid="lb-rw" /> Restoration</label>
                    </div>
                  </div>
                </div>
              )}

              {step === 1 && (
                <div className="space-y-4" data-testid="lb-step-1">
                  <div className="grid grid-cols-2 gap-3">
                    <div><label className={lbl}>Road / location name</label><input value={form.road_name} onChange={(e) => set("road_name", e.target.value)} className={inp} data-testid="lb-road" /></div>
                    <div><label className={lbl}>Road ID</label><input value={form.road_id} onChange={(e) => set("road_id", e.target.value)} className={inp} data-testid="lb-roadid" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><label className={lbl}>Carriageway</label><input value={form.carriageway} onChange={(e) => set("carriageway", e.target.value)} placeholder="LHS / both" className={inp} data-testid="lb-carriageway" /></div>
                    <div><label className={lbl}>Chainage start</label><input value={form.chainage_start} onChange={(e) => set("chainage_start", e.target.value)} className={inp} data-testid="lb-chstart" /></div>
                    <div><label className={lbl}>Chainage finish</label><input value={form.chainage_end} onChange={(e) => set("chainage_end", e.target.value)} className={inp} data-testid="lb-chend" /></div>
                  </div>
                  <div><label className={lbl}>Where on asset (OWP/IWP)</label>
                    <select value={form.where_on_asset} onChange={(e) => set("where_on_asset", e.target.value)} className={inp} data-testid="lb-where">
                      <option value="">—</option>
                      {(meta.where_on_asset || []).map((w) => <option key={w} value={w}>{w}</option>)}
                    </select></div>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4" data-testid="lb-step-2">
                  <div><label className={lbl}>Damage type</label>
                    <select value={form.damage_type} onChange={(e) => set("damage_type", e.target.value)} className={inp} data-testid="lb-damage">
                      <option value="">—</option>
                      {(meta.damage_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
                    </select></div>
                  <div><label className={lbl}>Standard treatment ({region})</label>
                    <select value={form.treatment_type} onChange={(e) => onTreatment(e.target.value)} className={inp} data-testid="lb-treatment">
                      <option value="">—</option>
                      {meta.treatments.map((t) => <option key={t.code} value={t.name}>{t.name}{t.rates?.[region] != null ? ` — $${t.rates[region]}/${t.unit}` : ""}</option>)}
                    </select></div>
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div><label className={lbl}>Unit</label><input value={form.unit_of_measure} onChange={(e) => set("unit_of_measure", e.target.value)} className={inp} data-testid="lb-uom" /></div>
                    <div><label className={lbl}>Quantity</label><input type="number" step="any" value={form.quantity} onChange={(e) => onQty(e.target.value)} className={inp} data-testid="lb-qty" /></div>
                    <div><Mono className="text-[0.5rem] text-white/45 block mb-1.5">Verified rate</Mono>
                      <div className="font-mono text-gold-300 text-sm py-2.5" data-testid="lb-rate">{rate != null ? `$${rate}/${selectedTreatment.unit}` : "—"}</div></div>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-4" data-testid="lb-step-3">
                  <div className="grid grid-cols-3 gap-3 items-end">
                    <div><label className={lbl}>CET benchmark ($)</label><input type="number" step="any" value={form.cet_cost} onChange={(e) => set("cet_cost", e.target.value)} className={inp} data-testid="lb-cet" /></div>
                    <div><label className={lbl}>Council cost ($)</label><input type="number" step="any" value={form.council_cost} onChange={(e) => set("council_cost", e.target.value)} className={inp} data-testid="lb-council" /></div>
                    <div><Mono className="text-[0.5rem] text-white/45 block mb-1.5">Variance</Mono>
                      <div className={`font-mono text-sm py-2.5 ${variance > 0 ? "text-red-300" : variance < 0 ? "text-emerald-300" : "text-white/60"}`} data-testid="lb-variance">{variance == null ? "—" : `${variance >= 0 ? "+" : ""}${variance.toFixed(0)}%`}</div></div>
                  </div>
                  <div><label className={lbl}>Cost justification</label>
                    <textarea rows={2} value={form.cost_justification} onChange={(e) => set("cost_justification", e.target.value)} className={inp + " resize-none"} data-testid="lb-justification" /></div>
                  {needJust && <div className="flex items-center gap-2 text-xs text-amber-300" data-testid="lb-just-warning"><AlertTriangle className="h-3.5 w-3.5" /> Variance is {Math.abs(variance).toFixed(0)}% — a justification is required for assessor review.</div>}
                  <div><label className={lbl}>Notes</label><textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} className={inp + " resize-none"} data-testid="lb-notes" /></div>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5" data-testid="lb-step-4">
                  <div className="border border-gold-500/20 divide-y divide-gold-500/10">
                    {summary.map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between px-4 py-2.5 text-sm">
                        <Mono className="text-[0.5rem] text-white/40">{k}</Mono>
                        <span className={`${v === "—" ? "text-white/30" : "text-white/85"} text-right`}>{v}</span>
                      </div>
                    ))}
                  </div>
                  <div>
                    <Mono className="text-[0.55rem] text-gold-400/80 block mb-2">Line review decision</Mono>
                    <div className="grid grid-cols-3 gap-2">
                      {[["approved", "Approve line", CheckCircle2, "border-emerald-400/50 text-emerald-300", "bg-emerald-400/15"],
                        ["pending", "Leave pending", Loader2, "border-white/25 text-white/60", "bg-white/10"],
                        ["flagged", "Flag issue", Flag, "border-red-400/50 text-red-300", "bg-red-400/15"]].map(([val, label, Icon, base, active]) => (
                        <button key={val} onClick={() => setDecision(val)} data-testid={`lb-decision-${val}`}
                          className={`border py-2.5 text-sm flex items-center justify-center gap-2 transition-colors ${base} ${decision === val ? active : "bg-transparent opacity-60 hover:opacity-100"}`}>
                          <Icon className="h-4 w-4" /> {label}
                        </button>
                      ))}
                    </div>
                    {decision !== "approved" && (
                      <input value={reviewNote} onChange={(e) => setReviewNote(e.target.value)} placeholder={decision === "flagged" ? "What needs fixing on this line?" : "Review note (optional)"} className={inp + " mt-2"} data-testid="lb-review-note" />
                    )}
                  </div>
                  {err && <div className="text-red-300 text-sm" data-testid="lb-error">{err}</div>}
                </div>
              )}

              {/* nav */}
              <div className="flex items-center justify-between pt-2 border-t border-gold-500/15">
                <button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} data-testid="lb-back"
                  className="inline-flex items-center gap-1.5 text-white/55 hover:text-gold-300 text-sm disabled:opacity-30 transition-colors">
                  <ChevronLeft className="h-4 w-4" /> Back
                </button>
                {step < 4 ? (
                  <button onClick={() => canNext && setStep((s) => s + 1)} disabled={!canNext} data-testid="lb-next"
                    className="bg-gold-500 text-navy-950 font-semibold px-6 py-2.5 rounded-sm hover:bg-gold-400 transition-colors disabled:opacity-40 inline-flex items-center gap-1.5">
                    Next <ChevronRight className="h-4 w-4" />
                  </button>
                ) : (
                  <button onClick={save} disabled={saving} data-testid="lb-save"
                    className="bg-gold-500 text-navy-950 font-semibold px-6 py-2.5 rounded-sm hover:bg-gold-400 transition-colors disabled:opacity-50 inline-flex items-center gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save line
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
